/**
 * Classify Live gateway WebSocket messages for backpressure handling.
 * Video frames are droppable; audio, text, session control, and tool messages are not.
 */

export type LiveGatewaySendPriority = 'critical' | 'normal' | 'droppable'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

/** True when message is a webcam/screen JPEG frame with no audio or text in the same payload. */
export function isDroppableVideoFrameMessage(message: unknown): boolean {
  if (!isRecord(message)) return false
  if (message.type !== 'realtime_input') return false

  const payload = message.payload
  if (!isRecord(payload)) return false

  const hasVideo = isRecord(payload.video) || Array.isArray(payload.video)
  if (!hasVideo) return false

  const hasAudio =
    (Array.isArray(payload.chunks) && payload.chunks.length > 0) ||
    payload.audioStreamEnd === true
  const hasText = typeof payload.text === 'string' && payload.text.trim().length > 0

  return !hasAudio && !hasText
}

export function getLiveGatewaySendPriority(message: unknown): LiveGatewaySendPriority {
  if (isDroppableVideoFrameMessage(message)) return 'droppable'

  if (!isRecord(message)) return 'normal'

  const type = String(message.type || '')
  if (
    type === 'session_start' ||
    type === 'session_resume' ||
    type === 'session_close' ||
    type === 'tool_response' ||
    type === 'CONTEXT_UPDATE'
  ) {
    return 'critical'
  }

  if (type === 'realtime_input') {
    const payload = message.payload
    if (isRecord(payload)) {
      const hasAudio =
        (Array.isArray(payload.chunks) && payload.chunks.length > 0) ||
        payload.audioStreamEnd === true
      const hasText = typeof payload.text === 'string' && payload.text.trim().length > 0
      if (hasAudio || hasText) return 'critical'
    }
  }

  return 'normal'
}
